import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { authApi } from '../api/authApi';
import { useAuth } from './AuthContext';

const SymbolNotesContext = createContext(null);
const STORAGE_KEY = 'lt-symbol-notes';

const loadLocal = () => {
  try {
    const raw = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
    return raw && typeof raw === 'object' && !Array.isArray(raw) ? raw : {};
  } catch {
    return {};
  }
};

const persistLocal = (notes) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(notes));
  } catch {
    // ignore
  }
};

export const SymbolNotesProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [notes, setNotes] = useState(() => loadLocal());
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      setHydrated(true);
      return undefined;
    }

    let cancelled = false;
    (async () => {
      try {
        const response = await authApi.getPreferences();
        const remote = response.data?.symbolNotes;
        if (!cancelled && remote && typeof remote === 'object' && !Array.isArray(remote)) {
          setNotes(remote);
          persistLocal(remote);
        }
      } catch {
        // keep local
      } finally {
        if (!cancelled) setHydrated(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated]);

  useEffect(() => {
    persistLocal(notes);
    if (!hydrated || !isAuthenticated) return undefined;
    const timer = setTimeout(() => {
      authApi.savePreferences({ symbolNotes: notes }).catch(() => {});
    }, 500);
    return () => clearTimeout(timer);
  }, [notes, hydrated, isAuthenticated]);

  const getNote = useCallback(
    (symbol) => {
      if (!symbol) return null;
      return notes[String(symbol).trim().toUpperCase()] || null;
    },
    [notes]
  );

  const setNote = useCallback((symbol, text, color) => {
    const key = String(symbol || '').trim().toUpperCase();
    if (!key) return null;
    const trimmed = String(text || '').trim().slice(0, 2000);
    setNotes((prev) => {
      const next = { ...prev };
      if (!trimmed) {
        delete next[key];
        return next;
      }
      next[key] = {
        text: trimmed,
        color: color || prev[key]?.color || 'amber',
        updatedAt: new Date().toISOString(),
      };
      return next;
    });
    return key;
  }, []);

  const removeNote = useCallback((symbol) => {
    const key = String(symbol || '').trim().toUpperCase();
    if (!key) return;
    setNotes((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  const noteList = useMemo(
    () =>
      Object.entries(notes)
        .map(([symbol, note]) => ({ symbol, ...note }))
        .sort((a, b) => String(b.updatedAt || '').localeCompare(String(a.updatedAt || ''))),
    [notes]
  );

  const value = useMemo(
    () => ({
      notes,
      noteList,
      noteCount: noteList.length,
      getNote,
      setNote,
      removeNote,
      hasNote: (symbol) => Boolean(getNote(symbol)),
    }),
    [notes, noteList, getNote, setNote, removeNote]
  );

  return (
    <SymbolNotesContext.Provider value={value}>{children}</SymbolNotesContext.Provider>
  );
};

export const useSymbolNotes = () => {
  const ctx = useContext(SymbolNotesContext);
  if (!ctx) throw new Error('useSymbolNotes must be used within SymbolNotesProvider');
  return ctx;
};

export const NOTE_COLOR_STYLES = {
  amber: {
    panel: 'bg-[rgba(232,184,74,0.12)] border-[rgba(232,184,74,0.35)]',
    chip: 'bg-[rgba(232,184,74,0.2)] text-[var(--lt-warning)]',
  },
  mint: {
    panel: 'bg-[var(--lt-bull-dim)] border-[rgba(61,220,151,0.35)]',
    chip: 'bg-[var(--lt-bull-dim)] text-bull',
  },
  rose: {
    panel: 'bg-[var(--lt-bear-dim)] border-[rgba(240,113,103,0.35)]',
    chip: 'bg-[var(--lt-bear-dim)] text-bear',
  },
  slate: {
    panel: 'bg-canvas border-hairline',
    chip: 'bg-elevated text-muted',
  },
};

export const NoteBadge = ({ symbol, className = '' }) => {
  const { getNote } = useSymbolNotes();
  const note = getNote(symbol);
  if (!note?.text) return null;
  const preview = note.text.length > 80 ? `${note.text.slice(0, 80)}…` : note.text;
  return (
    <span
      className={`inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase tracking-wider border bg-[rgba(232,184,74,0.16)] text-[var(--lt-warning)] border-[rgba(232,184,74,0.35)] ${className}`}
      title={preview}
    >
      Note
    </span>
  );
};
